import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';

const COLUMN_ALIASES = {
    title: ['tittel', 'title', 'navn', 'name', 'arrangement', 'event'],
    location: ['anlegg', 'location', 'sted', 'venue', 'facility'],
    club: ['klubb', 'club', 'arrangør', 'organizer'],
    start: ['start', 'startdato', 'start date', 'fra', 'from'],
    end: ['slutt', 'end', 'sluttdato', 'end date', 'til', 'to'],
    competitionType: ['stevnetype', 'type', 'competition type', 'kategori'],
    status: ['status'],
};

const FIELD_LABELS = {
    title: 'Tittel / Title',
    location: 'Anlegg / Location',
    club: 'Klubb / Club',
    start: 'Start',
    end: 'Slutt / End',
    competitionType: 'Stevnetype / Type',
    status: 'Status',
};

function parseNorwegianDate(str) {
    if (!str || typeof str !== 'string') return null;
    const trimmed = str.trim();

    // Try DD.MM.YYYY HH:MM
    const dtMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})$/);
    if (dtMatch) {
        const [, day, month, year, hours, minutes] = dtMatch;
        const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), parseInt(hours, 10), parseInt(minutes, 10));
        return isNaN(d.getTime()) ? null : d;
    }

    // Try DD.MM.YYYY
    const dMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dMatch) {
        const [, day, month, year] = dMatch;
        const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), 0, 0);
        return isNaN(d.getTime()) ? null : d;
    }

    // Fallback: try native Date parsing (handles ISO format)
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
}

function formatDateForDisplay(date) {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
}

@observer
class CsvEventImport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: 1,
            csvText: '',
            headers: [],
            columnMapping: {},
            detectedMapping: {},
            selectedCalendarId: '',
            parsedEvents: [],
            selectedEvents: {},
            selectAll: true,
            isLoading: false,
            error: null,
            importResult: null,
            previousImports: [],
            replaceImportId: '',
        };
    }

    componentDidMount() {
        this.loadPreviousImports();
    }

    loadPreviousImports = async () => {
        const response = await util.fetchApi('/api/calendarevents/csv/imports', { method: 'GET' }, {});
        if (response?.status === 200 && response.data) {
            this.setState({ previousImports: response.data });
        }
    };

    handlePasteInput = (e) => {
        this.setState({
            csvText: e.target.value,
            error: null,
        });
    };

    handleXlsxUpload = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        this.setState({ isLoading: true, error: null });
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const bytes = new Uint8Array(ev.target.result);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i += 1) {
                binary += String.fromCharCode(bytes[i]);
            }
            const fileBase64 = btoa(binary);
            const response = await util.fetchApi('/api/calendarevents/xlsx/parse', { method: 'POST' }, { fileBase64 });
            if (response?.status === 200 && response.data?.tsv) {
                this.setState({ csvText: response.data.tsv, isLoading: false, error: null });
            } else {
                this.setState({ isLoading: false, error: response?.message || 'Kunne ikke lese xlsx-filen.' });
            }
        };
        reader.onerror = () => this.setState({ isLoading: false, error: 'Kunne ikke lese filen.' });
        reader.readAsArrayBuffer(file);
    };

    extractHeaders = () => {
        const { csvText, selectedCalendarId } = this.state;
        if (!csvText) return;
        if (!selectedCalendarId) {
            this.setState({ error: 'Please select a calendar first.' });
            return;
        }

        // Detect delimiter
        const firstLine = csvText.split('\n')[0];
        const counts = {
            ';': (firstLine.match(/;/g) || []).length,
            ',': (firstLine.match(/,/g) || []).length,
            '\t': (firstLine.match(/\t/g) || []).length,
        };
        const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

        // Parse headers
        let headers = firstLine.split(delimiter).map(h => h.trim().replace(/^\uFEFF/, ''));
        const headerCounts = {};
        headers = headers.map(h => {
            if (!headerCounts[h]) {
                headerCounts[h] = 1;
                return h;
            }
            headerCounts[h] += 1;
            return `${h} (${headerCounts[h]})`;
        });

        // Auto-detect column mappings
        const detectedMapping = {};
        const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
        const usedIndices = new Set();

        // Pass 1: exact matches
        Object.entries(COLUMN_ALIASES).forEach(([field, aliases]) => {
            for (const alias of aliases) {
                const idx = normalizedHeaders.findIndex((h, i) => !usedIndices.has(i) && h === alias);
                if (idx !== -1) {
                    detectedMapping[field] = headers[idx];
                    usedIndices.add(idx);
                    break;
                }
            }
        });

        // Pass 2: substring matches
        Object.entries(COLUMN_ALIASES).forEach(([field, aliases]) => {
            if (detectedMapping[field]) return;
            for (const alias of aliases) {
                const idx = normalizedHeaders.findIndex((h, i) => !usedIndices.has(i) && h.includes(alias));
                if (idx !== -1) {
                    detectedMapping[field] = headers[idx];
                    usedIndices.add(idx);
                    break;
                }
            }
        });

        this.setState({
            headers,
            detectedMapping,
            columnMapping: { ...detectedMapping },
            step: 2,
        });
    };

    handleMappingChange = (field, value) => {
        this.setState(prevState => ({
            columnMapping: {
                ...prevState.columnMapping,
                [field]: value || undefined,
            },
        }));
    };

    parseAndPreview = () => {
        const { csvText, columnMapping } = this.state;
        if (!csvText || !columnMapping.title || !columnMapping.start) return;

        // Detect delimiter
        const firstLine = csvText.split('\n')[0];
        const counts = {
            ';': (firstLine.match(/;/g) || []).length,
            ',': (firstLine.match(/,/g) || []).length,
            '\t': (firstLine.match(/\t/g) || []).length,
        };
        const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

        let headers = firstLine.split(delimiter).map(h => h.trim().replace(/^\uFEFF/, ''));
        const headerCounts = {};
        headers = headers.map(h => {
            if (!headerCounts[h]) {
                headerCounts[h] = 1;
                return h;
            }
            headerCounts[h] += 1;
            return `${h} (${headerCounts[h]})`;
        });

        const lines = csvText.split('\n').slice(1);
        const events = [];

        for (let i = 0; i < lines.length; i += 1) {
            const line = lines[i].trim();
            if (!line) continue;

            const cols = line.split(delimiter).map(c => c.trim());
            const getValue = (field) => {
                const headerName = columnMapping[field];
                if (!headerName) return '';
                const idx = headers.indexOf(headerName);
                return idx >= 0 ? (cols[idx] || '') : '';
            };

            const title = getValue('title');
            const locationVal = getValue('location');
            const club = getValue('club');
            const startStr = getValue('start');
            const endStr = getValue('end');
            const competitionType = getValue('competitionType');
            const statusVal = getValue('status');

            if (!title && !startStr) continue;

            const startDate = parseNorwegianDate(startStr);
            let endDate = parseNorwegianDate(endStr);

            // If end is missing or same as start, set minimum 3 hour duration
            if (startDate && (!endDate || endDate.getTime() === startDate.getTime())) {
                endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
            }

            // Compose body from extra fields
            const bodyParts = [];
            if (club) bodyParts.push(`Klubb: ${club}`);
            if (competitionType) bodyParts.push(`Stevnetype: ${competitionType}`);
            if (statusVal) bodyParts.push(`Status: ${statusVal}`);
            const body = bodyParts.join('\n');

            // Validation
            let error = null;
            if (!title) {
                error = 'Mangler tittel';
            } else if (!startDate) {
                error = 'Ugyldig startdato';
            } else if (endDate && startDate && endDate < startDate) {
                error = 'Sluttdato er før startdato';
            }

            events.push({
                title,
                location: locationVal,
                body,
                start: startDate ? startDate.toISOString() : null,
                end: endDate ? endDate.toISOString() : (startDate ? startDate.toISOString() : null),
                error,
                _rawClub: club,
                _rawType: competitionType,
                _rawStatus: statusVal,
            });
        }

        if (events.length > 500) {
            this.setState({ error: 'Maks 500 hendelser per import.' });
            return;
        }

        const selectedEvents = {};
        events.forEach((e, idx) => {
            selectedEvents[idx] = !e.error;
        });

        this.setState({
            parsedEvents: events,
            selectedEvents,
            selectAll: events.every(e => !e.error),
            step: 3,
            error: null,
        });
    };

    toggleEventSelection = (idx) => {
        this.setState(prevState => {
            const newSelected = {
                ...prevState.selectedEvents,
                [idx]: !prevState.selectedEvents[idx],
            };
            return {
                selectedEvents: newSelected,
                selectAll: Object.values(newSelected).every(v => v),
            };
        });
    };

    toggleSelectAll = () => {
        const { parsedEvents, selectAll } = this.state;
        const newSelectAll = !selectAll;
        const selectedEvents = {};
        parsedEvents.forEach((e, idx) => {
            selectedEvents[idx] = e.error ? false : newSelectAll;
        });
        this.setState({ selectedEvents, selectAll: newSelectAll });
    };

    doImport = async () => {
        const { parsedEvents, selectedEvents, selectedCalendarId, replaceImportId } = this.state;
        const { calendarEventStore } = this.props.stores;

        const eventsToImport = parsedEvents
            .filter((e, idx) => selectedEvents[idx] && !e.error)
            .map(e => ({
                title: e.title,
                body: e.body,
                start: e.start,
                end: e.end,
                location: e.location,
            }));

        if (eventsToImport.length === 0) {
            this.setState({ error: 'Ingen hendelser valgt for import.' });
            return;
        }

        this.setState({ isLoading: true, error: null });

        try {
            const response = await calendarEventStore.importCsvEvents(eventsToImport, selectedCalendarId, replaceImportId);

            if (response?.status === 201) {
                this.setState({
                    importResult: response.data || { created: eventsToImport.length },
                    step: 4,
                    isLoading: false,
                });
            } else {
                this.setState({
                    error: response?.message || 'Feil ved import',
                    isLoading: false,
                });
            }
        } catch (err) {
            this.setState({
                error: err.message || 'En feil oppstod',
                isLoading: false,
            });
        }
    };

    goToStep = (step) => {
        this.setState({ step });
    };

    resetWizard = () => {
        this.setState({
            step: 1,
            csvText: '',
            headers: [],
            columnMapping: {},
            detectedMapping: {},
            selectedCalendarId: '',
            parsedEvents: [],
            selectedEvents: {},
            selectAll: true,
            isLoading: false,
            error: null,
            importResult: null,
            replaceImportId: '',
        });
        this.loadPreviousImports();
    };

    renderStep1() {
        const { csvText, selectedCalendarId, error, previousImports, replaceImportId } = this.state;
        const { calendarStore } = this.props.stores;
        const { calendars = [] } = calendarStore;

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-file-import me-2' />
                    Steg 1: Lim inn data og velg kalender
                </h5>

                {error && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {error}
                    </div>
                )}

                <div class='mb-3'>
                    <label class='form-label'>Velg kalender</label>
                    <select
                        class='form-select'
                        value={selectedCalendarId}
                        onChange={(e) => this.setState({ selectedCalendarId: e.target.value })}
                    >
                        <option value=''>-- Velg kalender --</option>
                        {calendars.map(c => (
                            <option key={c.calendarId || c.id} value={c.calendarId || c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {previousImports.length > 0 && (
                    <div class='mb-3'>
                        <label class='form-label'>Erstatt tidligere import (valgfritt)</label>
                        <select
                            class='form-select'
                            value={replaceImportId}
                            onChange={(e) => this.setState({ replaceImportId: e.target.value })}
                        >
                            <option value=''>-- Ny import (ikke erstatt) --</option>
                            {previousImports.map(imp => {
                                const d = new Date(imp.importDate);
                                const dateStr = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                const cal = calendars.find(c => (c.calendarId || c.id) === imp.calendarId);
                                const calName = cal ? cal.name : imp.calendarId;
                                return (
                                    <option key={imp.importId} value={imp.importId}>
                                        {dateStr} - {calName} ({imp.eventCount} hendelser)
                                    </option>
                                );
                            })}
                        </select>
                        <small class='text-body-secondary'>
                            Velg en tidligere import for a slette de gamle hendelsene og erstatte med nye.
                        </small>
                    </div>
                )}

                <div class='mb-3'>
                    <label class='form-label'>Last opp Excel-fil (.xlsx)</label>
                    <input
                        type='file'
                        accept='.xlsx'
                        class='form-control'
                        onChange={this.handleXlsxUpload}
                    />
                    <small class='text-body-secondary'>
                        Last opp en xlsx-terminliste (f.eks. fra skyting.no). Innholdet fylles inn i feltet under, og du kan justere ved behov.
                    </small>
                </div>

                <div class='text-center text-body-secondary mb-2'>
                    <small>&mdash; eller &mdash;</small>
                </div>

                <div class='mb-3'>
                    <label class='form-label'>Lim inn CSV / tab-separert data</label>
                    <textarea
                        class='form-control font-monospace'
                        rows={8}
                        placeholder={'Tittel\tAnlegg\tKlubb\tStart\tSlutt\tType\tStatus\nEksempel hendelse\tSted\tKlubb\t15.03.2026 09:00\t15.03.2026 17:00\tType\tGodkjent'}
                        value={csvText}
                        onInput={this.handlePasteInput}
                    />
                    <small class='text-body-secondary'>
                        Stottar tab-, semikolon- og komma-separerte data. Forste rad ma vare kolonneoverskrifter.
                    </small>
                </div>

                <div class='d-flex justify-content-end gap-2'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={this.props.onClose}
                    >
                        Avbryt
                    </button>
                    <button
                        type='button'
                        class='btn btn-primary'
                        disabled={!csvText || !selectedCalendarId}
                        onClick={this.extractHeaders}
                    >
                        Neste
                        <i class='fa-solid fa-arrow-right ms-2' />
                    </button>
                </div>
            </div>
        );
    }

    renderStep2() {
        const { headers, columnMapping, detectedMapping, error } = this.state;

        const hasTitle = !!columnMapping.title;
        const hasStart = !!columnMapping.start;

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-columns me-2' />
                    Steg 2: Koble kolonner
                </h5>

                {error && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {error}
                    </div>
                )}

                <p class='text-body-secondary mb-3'>
                    Koble CSV-kolonnene til hendelsesfelter. Auto-detekterte koblinger er ferdig utfylt.
                </p>

                <div class='row g-3'>
                    {Object.entries(FIELD_LABELS).map(([field, label]) => {
                        const isDetected = detectedMapping[field];
                        const isRequired = field === 'title' || field === 'start';

                        return (
                            <div class='col-md-6' key={field}>
                                <label class='form-label'>
                                    {label}
                                    {isRequired && <span class='text-danger ms-1'>*</span>}
                                    {isDetected && (
                                        <span class='badge bg-success ms-2'>
                                            <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                            Auto
                                        </span>
                                    )}
                                </label>
                                <select
                                    class='form-select'
                                    value={columnMapping[field] || ''}
                                    onChange={(e) => this.handleMappingChange(field, e.target.value)}
                                >
                                    <option value=''>-- Ikke koblet --</option>
                                    {headers.map(header => (
                                        <option key={header} value={header}>{header}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                </div>

                {(!hasTitle || !hasStart) && (
                    <div class='alert alert-warning mt-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        Du ma koble minst Tittel og Start for a importere hendelser.
                    </div>
                )}

                <div class='d-flex justify-content-between gap-2 mt-4'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={() => this.goToStep(1)}
                    >
                        <i class='fa-solid fa-arrow-left me-2' />
                        Tilbake
                    </button>
                    <button
                        type='button'
                        class='btn btn-primary'
                        disabled={!hasTitle || !hasStart}
                        onClick={this.parseAndPreview}
                    >
                        Forhandsvis
                        <i class='fa-solid fa-arrow-right ms-2' />
                    </button>
                </div>
            </div>
        );
    }

    renderStep3() {
        const { parsedEvents, selectedEvents, selectAll, isLoading, error, replaceImportId, previousImports } = this.state;

        const validCount = parsedEvents.filter(e => !e.error).length;
        const errorCount = parsedEvents.filter(e => e.error).length;
        const selectedCount = Object.entries(selectedEvents).filter(([, v]) => v).length;

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-eye me-2' />
                    Steg 3: Forhandsvisning
                </h5>

                {error && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {error}
                    </div>
                )}

                <div class='d-flex gap-3 mb-3'>
                    <div class='badge bg-success p-2'>
                        <i class='fa-solid fa-check-circle me-1' />
                        {validCount} klare
                    </div>
                    {errorCount > 0 && (
                        <div class='badge bg-danger p-2'>
                            <i class='fa-solid fa-times-circle me-1' />
                            {errorCount} feil
                        </div>
                    )}
                    <div class='badge bg-primary p-2'>
                        <i class='fa-solid fa-check-square me-1' />
                        {selectedCount} valgt for import
                    </div>
                </div>

                {replaceImportId && (() => {
                    const imp = previousImports.find(i => i.importId === replaceImportId);
                    return imp ? (
                        <div class='alert alert-warning mb-3'>
                            <i class='fa-solid fa-arrow-right-arrow-left me-2' />
                            <strong>Erstatter tidligere import:</strong> {imp.eventCount} eksisterende hendelser vil bli slettet og erstattet.
                        </div>
                    ) : null;
                })()}

                <div class='table-responsive' style='max-height: 400px; overflow-y: auto;'>
                    <table class='table table-sm table-hover mb-0'>
                        <thead class='sticky-top bg-body'>
                            <tr>
                                <th style='width: 40px;'>
                                    <input
                                        type='checkbox'
                                        class='form-check-input'
                                        checked={selectAll}
                                        onChange={this.toggleSelectAll}
                                    />
                                </th>
                                <th>Tittel</th>
                                <th>Sted</th>
                                <th>Start</th>
                                <th>Slutt</th>
                                <th>Info</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {parsedEvents.map((event, idx) => {
                                const rowClass = event.error ? 'table-danger' : (!selectedEvents[idx] ? 'text-muted' : '');
                                return (
                                    <tr key={idx} class={rowClass}>
                                        <td>
                                            <input
                                                type='checkbox'
                                                class='form-check-input'
                                                checked={selectedEvents[idx] || false}
                                                disabled={!!event.error}
                                                onChange={() => this.toggleEventSelection(idx)}
                                            />
                                        </td>
                                        <td>
                                            <small class='text-truncate d-block' style='max-width: 200px;' title={event.title}>
                                                {event.title || '-'}
                                            </small>
                                        </td>
                                        <td>
                                            <small class='text-truncate d-block' style='max-width: 150px;' title={event.location}>
                                                {event.location || '-'}
                                            </small>
                                        </td>
                                        <td><small>{formatDateForDisplay(event.start)}</small></td>
                                        <td><small>{formatDateForDisplay(event.end)}</small></td>
                                        <td>
                                            <small class='text-truncate d-block' style='max-width: 150px;' title={event.body}>
                                                {event._rawClub || event._rawType || '-'}
                                            </small>
                                        </td>
                                        <td>
                                            {event.error ? (
                                                <span class='badge bg-danger'>{event.error}</span>
                                            ) : (
                                                <span class='badge bg-success'>OK</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div class='d-flex justify-content-between gap-2 mt-4'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={() => this.goToStep(2)}
                    >
                        <i class='fa-solid fa-arrow-left me-2' />
                        Tilbake
                    </button>
                    <button
                        type='button'
                        class='btn btn-success'
                        disabled={selectedCount === 0 || isLoading}
                        onClick={this.doImport}
                    >
                        {isLoading ? (
                            <>
                                <span class='spinner-border spinner-border-sm me-2' />
                                Importerer...
                            </>
                        ) : (
                            <>
                                <i class='fa-solid fa-file-import me-2' />
                                Importer {selectedCount} hendelser
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    renderStep4() {
        const { importResult } = this.state;

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-check-circle text-success me-2' />
                    Import fullfort
                </h5>

                <div class='alert alert-success'>
                    <i class='fa-solid fa-check me-2' />
                    {importResult?.created || 0} hendelser ble importert.
                    {importResult?.deletedCount > 0 && (
                        <span> {importResult.deletedCount} tidligere hendelser ble erstattet.</span>
                    )}
                    {importResult?.errors?.length > 0 && (
                        <span> {importResult.errors.length} rader ble hoppet over pga. feil.</span>
                    )}
                </div>

                {importResult?.errors?.length > 0 && (
                    <div class='mb-3'>
                        <h6>Feil:</h6>
                        <ul class='list-group'>
                            {importResult.errors.map((err, idx) => (
                                <li key={idx} class='list-group-item list-group-item-danger'>
                                    Rad {err.row}: {err.error}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div class='d-flex justify-content-end gap-2'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={() => {
                            this.resetWizard();
                        }}
                    >
                        Ny import
                    </button>
                    <button
                        type='button'
                        class='btn btn-primary'
                        onClick={() => {
                            if (this.props.onComplete) {
                                this.props.onComplete();
                            }
                            if (this.props.onClose) {
                                this.props.onClose();
                            }
                        }}
                    >
                        Lukk
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { step } = this.state;

        return (
            <div class='card mb-3'>
                <div class='card-header d-flex justify-content-between align-items-center'>
                    <span>
                        <i class='fa-solid fa-file-import me-2' />
                        Importer hendelser fra CSV
                    </span>
                    <div class='d-flex align-items-center gap-2'>
                        <small class='text-body-secondary'>
                            Steg {step} av {step === 4 ? 4 : 3}
                        </small>
                        <button type='button' class='btn-close' onClick={this.props.onClose} />
                    </div>
                </div>
                <div class='card-body p-0'>
                    {step === 1 && this.renderStep1()}
                    {step === 2 && this.renderStep2()}
                    {step === 3 && this.renderStep3()}
                    {step === 4 && this.renderStep4()}
                </div>
            </div>
        );
    }
}

export default CsvEventImport;
